/**
 * `about` — the settings drawer's About row, which shipped as a dead `<div class="row">` with
 * nowhere to go. A **shell** surface (P6's, like the drawer that opens it), not a gate screen:
 * it reads no session state, calls no API, and renders identically online and offline.
 *
 * **Every outward link the app has now lands here.** The two contact values were always here;
 * D190's two legal links moved off the sidebar and joined them, because the sidebar is where a
 * resident goes to *do* something and this screen is where the product describes itself — and
 * four external destinations in a list of actions is four rows nobody was going to tap twice.
 * **D244's support line is the fifth**, and it arrived last for a reason worth keeping: the number
 * was published on four legal pages a day before anything in the app could reach it.
 *
 * None of the five is a row any more. A row is a labelled *value* — a description on one side and
 * something to read on the other — and there is nothing to read here beside the destination
 * itself. **D203 makes all five a glyph plus a translated label** (`email`/`website`/`phone`
 * joining the legal pair's own `privacy`/`terms`) rather than the raw address, hostname or
 * number: the label is what a reader in their own script reads, and the destination lives only
 * in the `href`. **The support line's own visible number is now gone with them**, which closes
 * D244's one exception rather than keeping it — the value travelled as an LTR token needing
 * `Bidi` isolation inside an RTL run (§7.2), and the hours line below already carries the one
 * fact a resident has to *read* rather than tap. `tel:` still dials the E.164 form, and the
 * number is published on the four legal pages for anyone who needs to copy it.
 *
 * **The three contact links sit on ONE row** (`.about-links-inline`), the legal pair stays
 * stacked. Three short labels read as one line of ways to reach us; the same three stacked read
 * as three separate destinations, which is what the block stopped being when the values went.
 * The row wraps rather than shrinking — at `--fs: 1.27` the Italian and Russian labels do not
 * fit 320px, and a wrapped pair over a single is better than a squeezed touch target.
 *
 * **The contact divider is `contactSectionTitle`, worded as "Additional details" rather than
 * "Get in touch" (§7.2's Hebrew: `פרטים נוספים`, not `יצירת קשר`)** — it introduces the email, the
 * website link and now the telephone, and "Get in touch" never fit the second one.
 *
 * All five are plain `<a>`s rather than app logic — one `mailto:`, one `tel:` and three external
 * `https:` — so the platform's own handler owns what happens next.
 *
 * The build identity no longer lives here — A5 (`ui-scale-refresh-handoff.md`) moved it to the
 * sidebar foot (`Drawer.tsx`'s `.drawer-version`), text rather than a row, so it reads on every
 * screen the drawer opens from rather than only after a trip to About.
 */
import { AppBar, Icon } from '../../components'
import { useLocale, useTranslation } from '../../i18n/I18nProvider'
import { useNavigation } from '../../router'
import { WEBSITE_URL, legalUrl } from '../../pwa/websiteLinks'

const CONTACT_EMAIL = 'contact@selogate.com'

/**
 * **D244's support line, reaching the app (D244(h) recorded its absence as a gap, not a
 * decision).** The number has been on all four legal pages and both `delete-account` pages since
 * 2026-09-03; the About screen's contact block offered the website and the email only, so a
 * resident *inside* the app had no human to call.
 *
 * **One form now, not two.** D244 shipped the local `072-2511605` beside the label as the one
 * contact link whose visible text was its value; that exception is withdrawn, so the screen is
 * D203 without a carve-out — every outward link is a glyph plus a translated label, and the
 * destination lives in the `href` alone. The `<Bidi>` this line needed goes with it: there is no
 * LTR token left in the row to reorder around a Hebrew run (§7.2). `SUPPORT_HOURS` below is the
 * one clock still rendered, and it keeps its isolation for exactly the reason this one lost it.
 */
const SUPPORT_PHONE_E164 = '+972722511605'

/**
 * **The hours are half the commitment and travel with the number.** A line that answers Sunday to
 * Thursday, 08:30–16:00 with nothing on the screen saying so is the defect
 * `breach-response-plan.md` §8 records from the other end — a notice that points a reader at a
 * line which answers tomorrow. The four published pages state the hours beside the number; so does
 * this screen, in `contactHint` rather than as a fourth centred line (the block is two centred
 * links and one grey caption, and a second caption would read as a second section).
 *
 * **U+2066 LRI … U+2069 PDI is `<bdi dir="ltr">` at string level, and it has to be**, because the
 * clock is interpolated into a *translated sentence* and `t()` returns a string — there is no
 * element to wrap. It is not decoration: `08:30–16:00` is two European-number runs either side of
 * an en dash the bidi algorithm treats as neutral, so inside a Hebrew paragraph the two runs swap
 * and a Hebrew reader is told the line answers `16:00–08:30`. It is the **only** isolate left on
 * this screen now that the support number is a label rather than a value — and the two went in
 * opposite directions for one reason: a number nobody has to read can live in an `href`, and a
 * clock is the one fact here that has to be read to be worth anything.
 */
const SUPPORT_HOURS = '\u2066' + '08:30\u201316:00' + '\u2069'

/**
 * The public legal pages (D190) are on the marketing site rather than in the app —
 * `website/terms.html` / `terms.he.html` and `website/privacy.html` / `privacy.he.html`, deployed
 * by that directory's own Cloudflare workflow. **`legalUrl` and the host it is built from moved to
 * `pwa/websiteLinks.ts` (D282)**, which gained a second caller — the Android-beta prompt in
 * `App.tsx` — and *one origin for everything the app links out to, so a hostname change is one edit
 * rather than a hunt* is exactly the note that made a second copy of the host unacceptable. The
 * Hebrew-or-English rule, and why it is an answer rather than a gap, is stated there.
 */

export function AboutScreen(): JSX.Element {
  const navigate = useNavigation()
  const { t } = useTranslation('about')
  const { locale } = useLocale()

  return (
    <>
      <AppBar title={t('title')} onBack={() => navigate.back()} />
      <div className="about-brand">
        {/*
          The generated brand mark already in `public/` (brand/README.md — never hand-edited
          here), painted by `global.css` as a **background** rather than carried by an `<img>`
          (D194). `favicon.svg` holds both palettes and picks between them with its own
          `prefers-color-scheme` block, which is the platform's answer — and since D188 the app's
          scheme is a *preference*, so on a dark phone set to light the mark came out graphite on
          cream with nothing in this document able to reach inside the image and say otherwise.
          The stylesheet chooses one of two palette-locked files by `[data-scheme]` instead, and
          only the file whose rule won is ever fetched. It is decorative either way: the wordmark
          below says the same thing in text, which is why this was `alt=""` before and is
          `aria-hidden` now.
        */}
        <div className="about-mark" aria-hidden="true" />
        <div className="about-wordmark">SeloGate</div>
        <div className="about-tagline">{t('tagline')}</div>
      </div>
      <div className="about-body">
        <p>{t('bodyHow')}</p>
      </div>
      <div className="section-divider">
        <span>{t('contactSectionTitle')}</span>
      </div>
      <div className="about-links about-links-inline">
        <a
          className="about-link"
          data-interactive="true"
          href={WEBSITE_URL}
          target="_blank"
          rel="noreferrer noopener"
        >
          <Icon name="globe" size={15} />
          {t('website')}
        </a>
        <a className="about-link" data-interactive="true" href={`mailto:${CONTACT_EMAIL}`}>
          <Icon name="mail" size={15} />
          {t('email')}
        </a>
        <a className="about-link" data-interactive="true" href={`tel:${SUPPORT_PHONE_E164}`}>
          <Icon name="phone" size={15} />
          {t('phone')}
        </a>
      </div>
      {/* `centered` because the block above it is: the divider says what the section is, and this
          line says what the two links are *for* — a start-aligned sentence under two centred
          links reads as the start of a new section rather than as their caption. */}
      <p className="hint centered">{t('contactHint', { hours: SUPPORT_HOURS })}</p>
      {/*
        `hreflang` is not set and `href` follows the *viewer's* locale, not the browser's: a user
        reading the app in Hebrew gets the Hebrew page. Each page carries a badge to the other, so
        a wrong guess costs one tap rather than stranding anyone — but there is nothing to guess
        here, since the locale is the one the user chose in the sidebar's Language row.
      */}
      <div className="section-divider">
        <span>{t('legalSectionTitle')}</span>
      </div>
      <div className="about-links">
        <a
          className="about-link"
          data-interactive="true"
          href={legalUrl('privacy', locale)}
          target="_blank"
          rel="noreferrer noopener"
        >
          <Icon name="shield" size={15} />
          {t('privacy')}
        </a>
        <a
          className="about-link"
          data-interactive="true"
          href={legalUrl('terms', locale)}
          target="_blank"
          rel="noreferrer noopener"
        >
          <Icon name="file-text" size={15} />
          {t('terms')}
        </a>
      </div>
      {/*
        The privacy sentence closes the legal block rather than introducing it, and it is a
        `hint centered` rather than body prose — the same treatment `contactHint` gets under the
        contact links, for the same reason: each section is two centred links with one grey line
        saying what they are about, so the two read as one pattern rather than as two layouts.
      */}
      <p className="hint centered">{t('bodyPrivacy')}</p>
    </>
  )
}
